// shared.jsx - Nav, Footer, Logo, DiscordCTA used by all pages.
// Compiled to dist/shared.js by build.mjs. Load before page bundle.

const { useState, useEffect, useCallback } = React;
const { useScrollRef } = window;

function getRelPrefix() {
  if (typeof window !== 'undefined' && window.location.pathname.includes('/compare')) {
    return '../';
  }
  return '';
}

function Logo() {
  const rel = getRelPrefix();
  return (
    <a href={`${rel}index.php`} style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 700, letterSpacing: -0.4, fontSize: 17, textDecoration: 'none', color: 'inherit' }}>
      <img src={`${rel}favicon.ico`} alt="" aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0 }} />
      <span style={{ fontFamily: 'var(--font-ui)' }}>PyK7VM</span>
    </a>
  );
}

function ThemeToggleBtn({ style = {}, showLabel = true, className = '' }) {
  const [theme, setTheme] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('light') ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    const handleThemeChange = (e) => {
      setTheme(e.detail?.theme || (document.documentElement.classList.contains('light') ? 'light' : 'dark'));
    };
    window.addEventListener('pk7vm-theme-change', handleThemeChange);
    return () => window.removeEventListener('pk7vm-theme-change', handleThemeChange);
  }, []);

  const toggle = () => {
    if (window.__PK7VM_THEME__?.toggleTheme) {
      const next = window.__PK7VM_THEME__.toggleTheme();
      setTheme(next);
    } else {
      const isLight = document.documentElement.classList.contains('light');
      const next = isLight ? 'dark' : 'light';
      if (next === 'light') {
        document.documentElement.classList.add('light');
        document.documentElement.setAttribute('data-theme', 'light');
      } else {
        document.documentElement.classList.remove('light');
        document.documentElement.setAttribute('data-theme', 'dark');
      }
      try { localStorage.setItem('pk7vm_theme', next); } catch (e) {}
      setTheme(next);
    }
  };

  const isLight = theme === 'light';

  return (
    <button
      type="button"
      onClick={toggle}
      className={`pk7vm-nav-theme-btn ${className}`}
      aria-label={isLight ? 'Switch to Dark mode' : 'Switch to Light mode'}
      title={isLight ? 'Switch to Dark mode (Ctrl+Shift+L)' : 'Switch to Light mode (Ctrl+Shift+L)'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(255, 255, 255, 0.05)',
        color: 'var(--text)',
        border: '1px solid var(--border)',
        borderRadius: 999,
        padding: showLabel ? '7px 14px 7px 11px' : '7px 10px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
        transition: 'all .2s ease',
        minHeight: 36,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
    >
      <span className="pk7vm-theme-icon-wrap" aria-hidden="true" style={{ width: 16, height: 16, position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg className="pk7vm-theme-icon pk7vm-theme-icon-sun" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', opacity: isLight ? 0 : 1, transform: isLight ? 'rotate(90deg) scale(0.5)' : 'rotate(0deg) scale(1)', transition: 'transform .35s, opacity .3s' }}>
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line>
          <line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
        <svg className="pk7vm-theme-icon pk7vm-theme-icon-moon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', opacity: isLight ? 1 : 0, transform: isLight ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)', transition: 'transform .35s, opacity .3s' }}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
      </span>
      {showLabel && <span className="pk7vm-theme-toggle-label">{isLight ? 'Dark' : 'Light'}</span>}
    </button>
  );
}
window.ThemeToggleBtn = ThemeToggleBtn;

function Nav() {
  const rel = getRelPrefix();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useScrollRef(useCallback((s) => setScrolled(s > 30), []));

  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (menuOpen) {
        document.body.classList.add('menu-open');
      } else {
        document.body.classList.remove('menu-open');
      }
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && menuOpen) setMenuOpen(false);
    };
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setMenuOpen(false);
        if (typeof document !== 'undefined') {
          document.body.classList.remove('menu-open');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      if (typeof document !== 'undefined') {
        document.body.classList.remove('menu-open');
      }
    };
  }, [menuOpen]);

  const links = [
    { label: 'Features', href: `${rel}index.php#features` },
    { label: 'Technology', href: `${rel}technology.php` },
    { label: 'Docs', href: `${rel}docs.php` },
    { label: 'Pricing', href: `${rel}pricing.php` },
    { label: 'FAQ', href: `${rel}faq.php` },
  ];

  return (
    <nav className="site-nav" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      height: 64,
      backdropFilter: 'blur(20px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(20px) saturate(1.2)',
      borderBottom: scrolled || menuOpen ? '1px solid var(--border)' : '1px solid transparent',
      transition: 'background .3s ease, border-color .3s ease',
    }}>
      <style>{`
        .site-nav {
          padding: 0 clamp(16px, 4vw, 56px);
          background: ${scrolled || menuOpen ? 'rgba(10,10,15,0.88)' : 'rgba(10,10,15,0.4)'};
        }
        html.light .site-nav {
          background: ${scrolled || menuOpen ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.6)'};
          border-bottom-color: ${scrolled || menuOpen ? 'rgba(15,23,42,0.08)' : 'transparent'};
        }
        .site-footer { padding: clamp(28px, 5vw, 48px) clamp(16px, 4vw, 56px); }
        
        .nv-desk {
          display: flex;
          gap: clamp(14px, 2vw, 24px);
          align-items: center;
          font-size: 13.5px;
          color: var(--text-dim);
        }
        .nv-ham {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 10px;
          flex-direction: column;
          gap: 5px;
          z-index: 1010;
        }
        .nv-ham span {
          display: block;
          width: 22px;
          height: 2px;
          background: var(--text);
          border-radius: 2px;
          transition: transform .3s ease, opacity .3s ease;
        }
        .nv-lnk {
          color: var(--text-dim);
          text-decoration: none;
          transition: color .2s ease;
          font-weight: 500;
        }
        .nv-lnk:hover {
          color: var(--text) !important;
        }

        .pk7vm-nav-theme-btn:hover {
          border-color: rgba(59, 130, 246, 0.45) !important;
          background: rgba(59, 130, 246, 0.08) !important;
          color: var(--text) !important;
        }
        html.light .pk7vm-nav-theme-btn {
          background: #ffffff !important;
          border-color: rgba(15, 23, 42, 0.12) !important;
          color: #0f172a !important;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05) !important;
        }
        html.light .pk7vm-nav-theme-btn:hover {
          border-color: rgba(37, 99, 235, 0.4) !important;
          background: #f8fafc !important;
          color: #2563eb !important;
        }

        @media (max-width: 900px) {
          .site-nav { padding: 0 16px !important; }
          .nv-desk { display: none !important; }
          .nv-ham { display: flex !important; }
          .site-footer { flex-direction: column !important; text-align: center !important; gap: 24px !important; }
          .site-footer-links { justify-content: center !important; }
        }

        @media (min-width: 901px) {
          .nv-desk { display: flex !important; }
          .nv-ham { display: none !important; }
          .mobile-nav-drawer { display: none !important; }
          .site-footer { flex-direction: row !important; text-align: left !important; }
          .site-footer-links { justify-content: flex-end !important; }
        }

        /* Mobile Drawer */
        .mobile-nav-drawer {
          position: fixed;
          top: 64px;
          left: 0;
          right: 0;
          bottom: 0;
          height: calc(100vh - 64px);
          height: calc(100dvh - 64px);
          background: rgba(10, 10, 15, 0.96);
          backdrop-filter: blur(28px);
          -webkit-backdrop-filter: blur(28px);
          z-index: 999;
          display: flex;
          flex-direction: column;
          padding: 24px clamp(16px, 5vw, 32px) 36px;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          animation: navFadeIn .3s ease forwards;
        }
        html.light .mobile-nav-drawer {
          background: rgba(255, 255, 255, 0.98);
        }

        @keyframes navFadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mobile-nav-link {
          color: var(--text);
          font-size: 18px;
          font-weight: 500;
          padding: 16px 8px;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid var(--border);
          transition: background .2s ease, color .2s ease;
          min-height: 52px;
        }
        .mobile-nav-link:active {
          background: rgba(59, 130, 246, 0.08);
          color: var(--brand-soft);
        }
      `}</style>

      <Logo />

      {/* Desktop Navigation */}
      <div className="nv-desk">
        {links.map(l => (
          <a key={l.label} href={l.href} className="nv-lnk">{l.label}</a>
        ))}
        <a href={`${rel}login.php`} className="nv-lnk">Sign in</a>
        <ThemeToggleBtn />
        <button
          onClick={() => window.location.href = `${rel}register.php`}
          style={{
            background: 'var(--text)', color: 'var(--bg)', border: 'none',
            padding: '9px 20px', borderRadius: 999, fontWeight: 600, fontSize: 13.5,
            cursor: 'pointer', letterSpacing: -0.1, transition: 'transform .2s ease, opacity .2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          Get started
        </button>
      </div>

      {/* Mobile Hamburger Toggle Button */}
      <button
        className="nv-ham"
        onClick={() => setMenuOpen(o => !o)}
        aria-label={menuOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
        aria-expanded={menuOpen}
        style={{ minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
      >
        <span style={{ transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }} />
        <span style={{ opacity: menuOpen ? 0 : 1 }} />
        <span style={{ transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
      </button>

      {/* Mobile Slide-Over Glass Drawer */}
      {menuOpen && (
        <div className="mobile-nav-drawer">
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 4 }}>
            {links.map(l => (
              <a
                key={l.label}
                href={l.href}
                className="mobile-nav-link"
                onClick={() => setMenuOpen(false)}
              >
                <span>{l.label}</span>
                <SvgArrowRight size={16} color="var(--text-muted)" />
              </a>
            ))}
            <a
              href={`${rel}login.php`}
              className="mobile-nav-link"
              onClick={() => setMenuOpen(false)}
            >
              <span>Sign in</span>
              <SvgArrowRight size={16} color="var(--text-muted)" />
            </a>
          </div>

          <div style={{ marginTop: 'auto', paddingTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <ThemeToggleBtn style={{ width: '100%', justifyContent: 'center', minHeight: 46 }} />
            <a
              href={`${rel}register.php`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--text)', color: 'var(--bg)',
                padding: '14px', borderRadius: 999, fontWeight: 600, fontSize: 15,
                textDecoration: 'none', textAlign: 'center', minHeight: 48,
              }}
            >
              Get started free
            </a>
            <a
              href="https://discord.gg/hpx6XkqmKM"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: 'rgba(88,101,242,0.12)', color: '#c5c9f5',
                border: '1px solid rgba(88,101,242,0.3)',
                padding: '13px', borderRadius: 999, fontWeight: 500, fontSize: 14,
                textDecoration: 'none', minHeight: 48,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#5865F2"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.033.053a19.985 19.985 0 0 0 6.001 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.201 13.201 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              <span>Join Community Discord</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}

function Footer() {
  const rel = getRelPrefix();
  const [dcHover, setDcHover] = useState(false);
  return (
    <footer className="site-footer" style={{
      borderTop: '1px solid var(--border)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: 'var(--text-muted)',
      fontSize: 13,
      flexWrap: 'wrap',
      gap: 20,
    }}>
      <Logo />
      <div className="site-footer-links" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <a href={`${rel}technology.php`} style={{ color: 'inherit', textDecoration: 'none' }}>Technology</a>
        <a href={`${rel}docs.php`} style={{ color: 'inherit', textDecoration: 'none' }}>Docs</a>
        <a href={`${rel}pricing.php`} style={{ color: 'inherit', textDecoration: 'none' }}>Pricing</a>
        <a href={`${rel}faq.php`} style={{ color: 'inherit', textDecoration: 'none' }}>FAQ</a>
        <a href={`${rel}privacy.php`} style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</a>
        <a href={`${rel}login.php`} style={{ color: 'inherit', textDecoration: 'none' }}>Sign In</a>
        <a
          href="https://discord.gg/hpx6XkqmKM"
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => setDcHover(true)}
          onMouseLeave={() => setDcHover(false)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: dcHover ? '#7289da' : 'var(--text-muted)', transition: 'color .25s', textDecoration: 'none' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.033.053a19.985 19.985 0 0 0 6.001 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.201 13.201 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
          Discord
        </a>
      </div>
      <span style={{ fontSize: 12.5 }}>© 2026 PyK7VM Systems. Enterprise code virtualization.</span>
    </footer>
  );
}

function DiscordCTA() {
  const [hover, setHover] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 1200); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position: 'fixed', bottom: 'clamp(16px, 3vw, 24px)', right: 'clamp(16px, 3vw, 24px)',
      zIndex: 999, opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity .6s cubic-bezier(.2,.7,.2,1), transform .6s cubic-bezier(.2,.7,.2,1)',
      pointerEvents: mounted ? 'auto' : 'none',
    }}>
      <style>{`
        .pk7vm-discord-pill {
          background: rgba(10, 10, 15, 0.92);
          border: 1px solid rgba(88, 101, 242, 0.32);
          box-shadow: 0 2px 14px rgba(0,0,0,0.5);
          color: var(--text-dim);
        }
        .pk7vm-discord-pill:hover {
          background: rgba(88, 101, 242, 0.22);
          border-color: rgba(88, 101, 242, 0.65);
          box-shadow: 0 4px 24px rgba(88, 101, 242, 0.35);
        }
        html.light .pk7vm-discord-pill {
          background: rgba(255, 255, 255, 0.92);
          border-color: rgba(88, 101, 242, 0.25);
          box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
          color: #334155;
        }
        html.light .pk7vm-discord-pill:hover {
          background: rgba(88, 101, 242, 0.1);
          border-color: rgba(88, 101, 242, 0.5);
          box-shadow: 0 6px 24px rgba(88, 101, 242, 0.2);
        }
      `}</style>
      <a
        href="https://discord.gg/hpx6XkqmKM"
        target="_blank"
        rel="noopener noreferrer"
        className="pk7vm-discord-pill"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          borderRadius: 999,
          padding: '8px 14px 8px 10px',
          textDecoration: 'none', cursor: 'pointer',
          transition: 'all .3s cubic-bezier(.2,.7,.2,1)',
          minHeight: 40,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill={hover ? '#7289da' : '#5865F2'} style={{ flexShrink: 0, transition: 'fill .3s' }}>
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.043.033.053a19.985 19.985 0 0 0 6.001 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.201 13.201 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
        <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-ui)', letterSpacing: -0.1, transition: 'color .3s' }}>Discord</span>
      </a>
    </div>
  );
}

function SvgCheck({ size = 14, color = '#10b981' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function SvgCross({ size = 14, color = '#f87171' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SvgInfo({ size = 14, color = 'var(--brand-soft)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function SvgArrowRight({ size = 14, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

window.Nav = Nav;
window.Footer = Footer;
window.Logo = Logo;
window.DiscordCTA = DiscordCTA;
window.SvgCheck = SvgCheck;
window.SvgCross = SvgCross;
window.SvgInfo = SvgInfo;
window.SvgArrowRight = SvgArrowRight;
